// src/ConsumerApp/Screens/ProductsList/ProductsList.js

import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar, CenteredContainer } from '../../DesignSystem';
import ProductCard from './components/ProductCard';
import BulkRfqCategoryBanner from './components/BulkRfqCategoryBanner';
import Bucket from '../Bucket/Bucket';

// Real SVGs from assets/icons
import X from '../../../assets/icons/X.svg';
import Package from '../../../assets/icons/Package.svg';

// Shared API Hooks
import {
  useGetSellingProducts,
  useGetBucketSummary,
  useGetFiltersByCategory,
} from '../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../api/savedList/savedList.hooks';
import { useGetDefaultDeliveryAddress } from '../../api/projects/project.hooks';

export default function ProductsList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Resolve category parameters
  const categoryId = location.state?.categoryId || searchParams.get('categoryId');
  const categoryName = location.state?.categoryName || searchParams.get('categoryName') || 'Products';

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const hubId = customer?.hubId;

  const [searchQuery, setSearchQuery] = useState('');
  const [isBucketOpen, setIsBucketOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [page, setPage] = useState(1);

  // 1. Data Queries & Saved Items Hooks
  const { data: rawAddress } = useGetDefaultDeliveryAddress(customerId);
  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: rawFiltersData } = useGetFiltersByCategory(categoryId);
  const { data: savedList = [] } = useGetSavedList(customerId);

  const addItemToSavedMutation = useAddItemToSavedList();
  const removeItemFromSavedMutation = useRemoveItemFromSavedList();

  const attributeValueIds = useMemo(() => appliedFilters.map((f) => f.optionId), [appliedFilters]);

  const {
    data: rawProductData,
    isLoading: isProductsLoading,
    isError,
    error,
  } = useGetSellingProducts({
    customer_id: customerId,
    product_category_id: categoryId,
    attribute_value_ids: attributeValueIds,
    page: page,
    page_size: 40,
  });

  // O(1) Lookup Set for Saved Products
  const savedItemsSet = useMemo(() => {
    const list = Array.isArray(savedList) ? savedList : savedList?.items || [];
    return new Set(
      list.map((item) => String(item.product_id || item.item_id || item._id))
    );
  }, [savedList]);

  // Bucket Item Map for Quantity Synchronization
  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const buyNowItems = bucketSummary?.buy_now_bucket?.items || [];
    buyNowItems.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: bItem.quantity,
          bucketId: bucketSummary?.buy_now_bucket?.bucket_id || bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  // Robust product array unwrapping
  const products = useMemo(() => {
    const list =
      rawProductData?.products ||
      rawProductData?.data?.products ||
      (Array.isArray(rawProductData) ? rawProductData : []);

    if (!searchQuery.trim()) return list;
    return list.filter((p) =>
      (p.product_name || p.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [rawProductData, searchQuery]);

  const isCementCategory = categoryName?.toLowerCase().includes('cement');

  const handleRemoveFilter = (id) => {
    setAppliedFilters((prev) => prev.filter((f) => f.optionId !== id));
  };

  // Production-grade Save/Unsave Toggle Handler
  const handleToggleSave = async (item, shouldSave) => {
    if (!customerId) {
      navigate('/consumer');
      return false;
    }
    const pid = String(item.product_id || item._id || item.id);
    try {
      if (shouldSave) {
        await addItemToSavedMutation.mutateAsync({
          customer_id: customerId,
          product_id: pid,
        });
      } else {
        await removeItemFromSavedMutation.mutateAsync({
          customer_id: customerId,
          product_id: pid,
        });
      }
      return true;
    } catch (err) {
      console.error('Failed to toggle saved item:', err);
      return false;
    }
  };

  return (
    <div style={styles.pageShell}>
      <Navbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      <main style={styles.mainFeed}>
        <CenteredContainer>
          <div style={styles.layout}>
            {/* Header / Breadcrumbs */}
            <div style={styles.headerBlock}>
              <div>
                <span style={styles.breadcrumb} onClick={() => navigate('/consumer/HomeScreen')}>
                  Home / Categories /
                </span>
                <h1 style={styles.categoryTitle}>{categoryName}</h1>
              </div>
              <span style={styles.itemsCountBadge}>
                {rawProductData?.pagination?.total_items || products.length} items available
              </span>
            </div>

            {/* Active Filter Tags */}
            {appliedFilters.length > 0 && (
              <div style={styles.tagsRow}>
                {appliedFilters.map((f) => (
                  <div key={f.optionId} style={styles.filterTag}>
                    <span>{f.label}</span>
                    <button style={styles.tagCloseBtn} onClick={() => handleRemoveFilter(f.optionId)}>
                      <img src={X} alt="remove" style={{ width: 10, height: 10 }} />
                    </button>
                  </div>
                ))}
                <button style={styles.clearAllBtn} onClick={() => setAppliedFilters([])}>
                  Clear all
                </button>
              </div>
            )}

            {/* Product Feed / Error / Loading States */}
            {isProductsLoading ? (
              <div style={styles.loadingBox}>Loading catalog items...</div>
            ) : isError ? (
              <div style={styles.errorBox}>
                Unable to load products. {error?.message || 'Please try again.'}
              </div>
            ) : products.length === 0 ? (
              <div style={styles.emptyBox}>
                <img src={Package} alt="empty" style={{ width: 64, height: 64, opacity: 0.3 }} />
                <h3 style={{ margin: '12px 0 4px 0', color: '#2B2A29' }}>No products found</h3>
                <p style={{ margin: 0, color: '#707B8C', fontSize: 13 }}>
                  Try changing your search term or clearing applied filters.
                </p>
              </div>
            ) : (
              <div style={styles.catalogFlow}>
                {/* 1st Row of Products */}
                <div style={styles.productsGrid}>
                  {products.slice(0, 4).map((p) => {
                    const pid = String(p.product_id || p._id);
                    return (
                      <ProductCard
                        key={pid}
                        item={p}
                        customerId={customerId}
                        hubId={hubId}
                        savedBucketQuantity={bucketItemsMap.get(pid)?.quantity}
                        bucketId={bucketItemsMap.get(pid)?.bucketId}
                        isSavedItem={savedItemsSet.has(pid)}
                        onToggleSave={handleToggleSave}
                        onItemClick={() =>
                          navigate('/consumer/productdetails', { state: { productId: pid } })
                        }
                      />
                    );
                  })}
                </div>

                {/* Cement Bulk WhatsApp Banner */}
                {isCementCategory && <BulkRfqCategoryBanner categoryName={categoryName} />}

                {/* Remaining Products */}
                {products.length > 4 && (
                  <div style={styles.productsGrid}>
                    {products.slice(4).map((p) => {
                      const pid = String(p.product_id || p._id);
                      return (
                        <ProductCard
                          key={pid}
                          item={p}
                          customerId={customerId}
                          hubId={hubId}
                          savedBucketQuantity={bucketItemsMap.get(pid)?.quantity}
                          bucketId={bucketItemsMap.get(pid)?.bucketId}
                          isSavedItem={savedItemsSet.has(pid)}
                          onToggleSave={handleToggleSave}
                          onItemClick={() =>
                            navigate('/consumer/productdetails', { state: { productId: pid } })
                          }
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </CenteredContainer>
      </main>

      <Bucket isOpen={isBucketOpen} onClose={() => setIsBucketOpen(false)} />
    </div>
  );
}

const styles = {
  pageShell: {
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  mainFeed: {
    width: '100%',
    padding: '24px 0 60px 0',
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  headerBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 12,
  },
  breadcrumb: {
    fontSize: 12,
    color: '#707B8C',
    cursor: 'pointer',
    display: 'block',
    marginBottom: 4,
  },
  categoryTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: '#2B2A29',
    margin: 0,
  },
  itemsCountBadge: {
    fontSize: 13,
    color: '#707B8C',
    fontWeight: '600',
  },
  tagsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  filterTag: {
    backgroundColor: '#F5F3EF',
    border: '1px solid #CBD5E1',
    borderRadius: 20,
    padding: '4px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#2B2A29',
  },
  tagCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
  },
  clearAllBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#E9001E',
    fontSize: 12,
    fontWeight: '700',
  },
  catalogFlow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 16,
  },
  loadingBox: {
    padding: 60,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 14,
  },
  errorBox: {
    padding: 60,
    textAlign: 'center',
    color: '#E9001E',
    fontSize: 14,
  },
  emptyBox: {
    padding: 60,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #E2E8F0',
  },
};